/**
 * @fileoverview Logo file state and drag-and-drop handlers for corporate identity.
 * @module components/AppointmentRegistrationSections/CorporateIdentity/useCorporateIdentityLogo
 */
import { useCallback, useState } from "react";
import { validateLogoFile } from "../../../utils/corporateIdentityUtils.js";
import { createFileObjectFromUrl } from "../../../utils/common";

/**
 * @param {Object} options
 * @param {Object} options.company
 * @param {Function} options.t
 * @param {Function} options.handleInputChange
 */
export const useCorporateIdentityLogo = ({ company, t, handleInputChange }) => {
  const [logoFile, setLogoFile] = useState(
    createFileObjectFromUrl(company?.logo) || null,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const isUploading = false;

  const applyLogoSelection = useCallback(
    (file) => {
      const validationError = validateLogoFile(file, t);
      if (validationError) {
        setError(validationError);
        setLogoFile(null);
        handleInputChange("logo", null);
        return;
      }
      setLogoFile(file);
      setError("");
      handleInputChange("logo", file);
    },
    [handleInputChange, t],
  );

  const handleFileChange = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (file) {
        applyLogoSelection(file);
      }
    },
    [applyLogoSelection],
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      setIsDragging(false);
      const file = event.dataTransfer.files[0];
      if (file) {
        applyLogoSelection(file);
      }
    },
    [applyLogoSelection],
  );

  const handleFileClick = useCallback(() => {
    document.getElementById("logoFileInput").click();
  }, []);

  return {
    logoFile,
    isUploading,
    isDragging,
    error,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileClick,
  };
};
